/**
 * Esquemas del módulo **Planes** (AF §4.1, §4.3, §4.7): catálogo de planes, lista de
 * precios versionada y promociones.
 *
 * Fuente única entre backend, SPA y app (Arquitectura §3.3). Regla que estos esquemas
 * hacen cumplir desde el borde: **ninguna cifra se tipea suelta** — un precio entra por
 * la lista versionada (RN-PLA-10) y la vigencia es SIEMPRE el día 1 de un mes.
 */
import { z } from "zod";
import { ACCESS_PROFILES, LOCATION_SCOPES, MAX_INSTALLMENTS, PLAN_KINDS, PLAN_PLATFORM_REQUIREMENTS, PRICE_MEDIUMS, PROMOTION_TYPES, } from "../enums.js";
import { LEGACY_REFORMER_ONLY_KIND } from "../plan-access.js";
import { dateISO, moneySchema, objectId, pagination, reasonRequired } from "./base.js";
/**
 * RN-PLA-14 (flyers octubre 2026): una cantidad de cuotas es un entero de 1 a 12. Cuántas
 * admite cada plan lo valida el backend contra su tipo; cuáles se venden, la lista vigente.
 */
export const installmentsSchema = z
    .number()
    .int("La cantidad de cuotas es un número entero")
    .min(1, `Las cuotas van de 1 a ${MAX_INSTALLMENTS}`)
    .max(MAX_INSTALLMENTS, `Las cuotas van de 1 a ${MAX_INSTALLMENTS}`);
// ── Catálogo de planes (RN-PLA-01..05) ──────────────────────────────────────
export const planWeeklyInclusion = z.object({
    disciplineId: objectId,
    /** RN-PLA-01: Kangoo/Pole/Bungee 2×, Yoga Aéreo/Reformer 1× sobre el plan base. */
    perWeek: z.number().int().min(0).max(7),
});
export const planAccessCoverage = z.object({
    /** RN-ACC-11: Solo Reformer sólo entra a locales de Pilates; Musculación, a `strength`. */
    locationScope: z.enum(LOCATION_SCOPES).default("all"),
    weeklyEntryLimit: z.number().int().min(1).max(21).nullable().default(null),
});
/**
 * RN-PLA-35 (Viva Infinity, octubre 2026): «1 actividad que elijas con clases ilimitadas».
 * El socio la elige al contratar entre `eligibleDisciplineIds`; esa actividad no tiene
 * límite semanal pero sí un tope de `dailyLimit` clases por día operativo.
 */
export const planChosenUnlimited = z.object({
    enabled: z.boolean().default(false),
    eligibleDisciplineIds: z.array(objectId).max(50).default([]),
    dailyLimit: z.number().int().min(1).max(3).default(1),
});
/** Un plan sin actividad a elección: es el default de todo plan anterior a Infinity. */
export const CHOSEN_UNLIMITED_DISABLED = {
    enabled: false,
    eligibleDisciplineIds: [],
    dailyLimit: 1,
};
export const createPlanBody = z
    .object({
    code: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9_]+$/, "El código va en minúsculas, sin espacios"),
    name: z.string().trim().min(2).max(120),
    kind: z.enum(PLAN_KINDS),
    description: z.string().max(2000).default(""),
    /** RN-PLA-04: quincena 15, semana 7. El mensual usa ancla, no duración fija. */
    durationDays: z.number().int().min(1).max(400).nullable().default(null),
    durationMonths: z.number().int().min(1).max(24).nullable().default(null),
    /** RN-PLA-13: anual +15, semestral +7. */
    giftDays: z.number().int().min(0).max(60).default(0),
    /** RN-PLA-14: largos 1..12 (anual hasta 6, semestral hasta 3 en octubre 2026), resto 1. */
    maxInstallments: installmentsSchema.default(1),
    freezesPrice: z.boolean().default(false),
    transferableOnce: z.boolean().default(false),
    waivesEnrollmentFee: z.boolean().default(false),
    canFixClasses: z.boolean().default(false),
    /** RN-PLA-19: anual 30, semestral 15 por defecto (flyers octubre 2026); configurable. */
    vacationFreezeDays: z.number().int().min(0).max(60).default(0),
    accessCoverage: planAccessCoverage.default({ locationScope: "all", weeklyEntryLimit: null }),
    weeklyInclusions: z.array(planWeeklyInclusion).default([]),
    maxFixedSlots: z.number().int().min(0).max(7).nullable().default(null),
    /** RN-PLA-02/33/34: el alcance del plan, separado de su duración (`kind`). */
    accessProfile: z.enum(ACCESS_PROFILES).default("full"),
    /** Sólo con `disciplines_only`: las únicas disciplinas que reserva (RN-CLA-26). */
    allowedDisciplineIds: z.array(objectId).max(50).default([]),
    /**
     * RN-PLA-35: bolsa semanal de clases extra en las actividades especiales, compartida
     * entre todas (Infinity: 2 en total, no 2 por actividad). `null` = el plan no tiene bolsa.
     */
    weeklyExtraPool: z.number().int().min(1).max(14).nullable().default(null),
    /** RN-PLA-35: actividad a elección ilimitada, con tope diario. */
    chosenUnlimited: planChosenUnlimited.default(CHOSEN_UNLIMITED_DISABLED),
    /**
     * RN-PLA-36: complemento de plataforma. Se vende sólo a un visitante activo de esa
     * plataforma y no da acceso por sí mismo: el ingreso lo sigue pagando el convenio.
     */
    requiresPlatform: z.enum(PLAN_PLATFORM_REQUIREMENTS).nullable().default(null),
    /**
     * RN-PLA-37: hasta cuándo el gimnasio pensaba ofrecer el plan (ej. los planes de
     * octubre, hasta el 31/10). `null` = sin vencimiento. Es SÓLO un aviso: un plan vencido
     * se sigue vendiendo y cobrando con el precio de la lista vigente.
     */
    offerValidUntil: dateISO.nullable().default(null),
})
    .refine((p) => !(p.kind === "monthly" && p.transferableOnce), {
    message: "La membresía mensual es intransferible (RN-PLA-15, ficha Viva)",
    path: ["transferableOnce"],
})
    // `reformer_only` existe sólo por los planes viejos: el alcance va en `accessProfile`.
    .refine((p) => p.kind !== LEGACY_REFORMER_ONLY_KIND, {
    message: "Para un plan de sólo Reformer elegí la duración y el alcance «sólo algunas disciplinas»",
    path: ["kind"],
})
    .superRefine((p, ctx) => {
    for (const issue of planAccessIssues(p))
        ctx.addIssue({ code: "custom", ...issue });
});
/**
 * Coherencia del alcance (RN-PLA-33, RN-CLA-26): la usan el esquema de alta y el backend
 * al editar (sobre el plan ya fusionado), así la regla es una sola.
 */
export function planAccessIssues(p) {
    const issues = [];
    const profile = p.accessProfile ?? "full";
    const allowed = p.allowedDisciplineIds ?? [];
    if (profile === "disciplines_only" && allowed.length === 0) {
        issues.push({
            message: "Un plan de sólo algunas disciplinas necesita al menos una disciplina",
            path: ["allowedDisciplineIds"],
        });
    }
    if (profile !== "disciplines_only" && allowed.length > 0) {
        issues.push({
            message: "Las disciplinas permitidas sólo aplican a un plan de sólo algunas disciplinas",
            path: ["allowedDisciplineIds"],
        });
    }
    if (profile === "strength_only") {
        if (p.accessCoverage?.locationScope !== "strength") {
            issues.push({
                message: "Un plan de musculación entra sólo a las salas de musculación",
                path: ["accessCoverage", "locationScope"],
            });
        }
        if ((p.weeklyInclusions ?? []).length > 0) {
            issues.push({
                message: "Un plan de musculación no incluye clases",
                path: ["weeklyInclusions"],
            });
        }
        if (p.weeklyExtraPool || p.chosenUnlimited?.enabled) {
            issues.push({
                message: "Un plan de musculación no incluye clases: ni extras ni actividad a elección",
                path: ["chosenUnlimited"],
            });
        }
    }
    // RN-PLA-35: la actividad a elección se elige de una lista que el plan define.
    const eligible = (p.chosenUnlimited?.eligibleDisciplineIds ?? []).map(String);
    if (p.chosenUnlimited?.enabled && eligible.length === 0) {
        issues.push({
            message: "La actividad a elección necesita al menos una disciplina elegible",
            path: ["chosenUnlimited", "eligibleDisciplineIds"],
        });
    }
    if (p.chosenUnlimited?.enabled && profile === "disciplines_only") {
        const allowedIds = new Set(allowed.map(String));
        if (eligible.some((id) => !allowedIds.has(id))) {
            issues.push({
                message: "Las actividades a elección tienen que estar entre las disciplinas del plan",
                path: ["chosenUnlimited", "eligibleDisciplineIds"],
            });
        }
    }
    return issues;
}
export const updatePlanBody = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    /**
     * RN-PLA-39: la duración se cambia sólo en un plan SIN ventas (el backend lo valida
     * contra las membresías). Con ventas se crea uno nuevo y se desactiva este.
     */
    kind: z.enum(PLAN_KINDS).optional(),
    description: z.string().max(2000).optional(),
    durationDays: z.number().int().min(1).max(400).nullable().optional(),
    durationMonths: z.number().int().min(1).max(24).nullable().optional(),
    giftDays: z.number().int().min(0).max(60).optional(),
    maxInstallments: installmentsSchema.optional(),
    freezesPrice: z.boolean().optional(),
    transferableOnce: z.boolean().optional(),
    waivesEnrollmentFee: z.boolean().optional(),
    canFixClasses: z.boolean().optional(),
    vacationFreezeDays: z.number().int().min(0).max(60).optional(),
    accessCoverage: planAccessCoverage.optional(),
    weeklyInclusions: z.array(planWeeklyInclusion).optional(),
    maxFixedSlots: z.number().int().min(0).max(7).nullable().optional(),
    accessProfile: z.enum(ACCESS_PROFILES).optional(),
    allowedDisciplineIds: z.array(objectId).max(50).optional(),
    weeklyExtraPool: z.number().int().min(1).max(14).nullable().optional(),
    chosenUnlimited: planChosenUnlimited.optional(),
    requiresPlatform: z.enum(PLAN_PLATFORM_REQUIREMENTS).nullable().optional(),
    /** RN-PLA-37: cambiarla vuelve a habilitar los avisos de vencimiento (RN-PLA-38). */
    offerValidUntil: dateISO.nullable().optional(),
    active: z.boolean().optional(),
});
/** Desactivar un plan es una decisión comercial: lleva motivo y queda como excepción. */
export const deactivatePlanBody = z.object({ reason: reasonRequired });
export const listPlansQuery = pagination.extend({
    active: z.enum(["true", "false"]).optional(),
    kind: z.enum(PLAN_KINDS).optional(),
});
// ── Lista de precios (RN-PLA-10/11) ─────────────────────────────────────────
export const priceListItem = z.object({
    planId: objectId,
    /** El valor preferencial por débito automático es `medium=debit` (consent. débito). */
    medium: z.enum(PRICE_MEDIUMS),
    installments: installmentsSchema.default(1),
    amount: moneySchema.min(0),
});
/** RN-PLA-12 / RN-SOC-29: matrícula propia de un plan (Infinity $35.000 vs. pase libre $20.000). */
export const priceListEnrollmentFee = z.object({
    planId: objectId,
    amount: moneySchema.min(0),
});
/**
 * RN-PLA-01 / RN-CLA-24 (flyers octubre 2026): el gimnasio publica el extra como **valor
 * mensual de UNA clase extra semanal**, distinto en efectivo y con otro medio. El pack de N
 * extras semanales vale N veces eso; la clase suelta, un cuarto.
 */
export const priceListExtra = z.object({
    disciplineId: objectId,
    medium: z.enum(PRICE_MEDIUMS),
    monthlyPerWeeklySlot: moneySchema.min(0),
});
/**
 * RN-PLA-10 (rel. 3.12): **la vigencia es siempre el día 1 de un mes calendario**.
 * Se valida acá, en el borde, para que no exista forma de crear una lista con vigencia
 * el 15 ni «por error de tipeo».
 */
const enrollmentFeesByPlan = z
    .array(priceListEnrollmentFee)
    .refine((rows) => new Set(rows.map((r) => r.planId)).size === rows.length, {
    message: "Cada plan tiene una sola matrícula propia",
});
const extrasByDiscipline = z
    .array(priceListExtra)
    .refine((rows) => new Set(rows.map((r) => `${r.disciplineId}:${r.medium}`)).size === rows.length, { message: "Cada disciplina tiene un solo precio de extra por medio de pago" });
export const createPriceListBody = z.object({
    effectiveFrom: dateISO,
    items: z.array(priceListItem).min(1),
    /** Matrícula general: la de todo plan que no tenga la suya en `enrollmentFeeByPlan`. */
    enrollmentFee: moneySchema.min(0),
    enrollmentFeeByPlan: enrollmentFeesByPlan.default([]),
    trialClassPrice: moneySchema.min(0),
    extrasByDiscipline: extrasByDiscipline.default([]),
    familyDiscountPct: z.number().int().min(0).max(100).default(0),
    /** Publicar de una (`active`) o dejarla en borrador. */
    publish: z.boolean().default(true),
});
/**
 * Edición de un borrador: sólo cambia lo que viaja. Los arreglos se redeclaran sin default
 * (con zod 4, `.partial()` conserva los `.default([])` y un PATCH de `items` vaciaría la
 * matrícula por plan y los extras).
 */
export const updatePriceListBody = createPriceListBody
    .partial()
    .omit({ effectiveFrom: true })
    .extend({
    enrollmentFeeByPlan: enrollmentFeesByPlan.optional(),
    extrasByDiscipline: extrasByDiscipline.optional(),
    familyDiscountPct: z.number().int().min(0).max(100).optional(),
});
export const listPriceListsQuery = pagination.extend({
    status: z.enum(["draft", "active", "superseded"]).optional(),
});
/** Consulta puntual: «¿cuánto vale este plan por este medio a esta fecha?» */
export const priceQuoteQuery = z.object({
    planId: objectId,
    medium: z.enum(PRICE_MEDIUMS).default("cash"),
    installments: z.coerce.number().int().min(1).max(MAX_INSTALLMENTS).default(1),
    /** RN-PLA-11: **la fecha que manda es la de VENCIMIENTO**, no la de pago. */
    dueDate: dateISO.optional(),
    familyDiscount: z
        .union([z.boolean(), z.enum(["true", "false"])])
        .transform((v) => v === true || v === "true")
        .optional(),
});
// ── Planes vigentes (Configuración → Planes y precios, tab Planes) ──────────
/**
 * `GET /api/planes/vigentes`: `current` calcula con la lista vigente hoy; `pending`, con
 * la lista sin publicar (borrador o programada) para previsualizar un aumento.
 */
export const planesVigentesQuery = z.object({
    prices: z.enum(["current", "pending"]).default("current"),
});
/** Un precio del plan en la lista: medio, cuotas e importe total (centavos). */
export const planPriceItem = z.object({
    medium: z.enum(PRICE_MEDIUMS),
    installments: installmentsSchema.default(1),
    amount: moneySchema.min(1, "El precio tiene que ser mayor a cero"),
});
/**
 * `PUT /api/planes/:id/precios` (RN-PLA-10, RN-PLA-39): los precios de UN plan en el
 * BORRADOR de la vigencia elegida. Nunca toca una lista publicada: si no hay borrador para
 * ese día 1, se crea copiando la vigente.
 */
export const planPricesBody = z.object({
    /** RN-PLA-10: siempre el día 1 de un mes (lo valida además el servicio). */
    effectiveFrom: dateISO,
    items: z
        .array(planPriceItem)
        .min(1, "Cargá al menos un precio")
        .refine((rows) => new Set(rows.map((r) => `${r.medium}:${r.installments}`)).size === rows.length, { message: "Cada medio y cantidad de cuotas va una sola vez" }),
    /** RN-PLA-12: matrícula propia del plan. `null` = paga la general de la lista. */
    enrollmentFee: moneySchema.min(0).nullable().default(null),
});
// ── Promociones (RN-PLA-20) ─────────────────────────────────────────────────
/**
 * Promo de referidos (octubre 2026: «referencianos y adquieren vos y tu referido un 10 %»).
 * El que compra recibe la promo; el socio que lo refirió, un crédito de
 * `referrerBenefitPct` % sobre su próxima cuota. Habilitada, el % va de 1 a 100.
 */
export const promotionReferral = z
    .object({
    enabled: z.boolean(),
    referrerBenefitPct: z.number().int().min(0).max(100),
})
    .refine((r) => !r.enabled || r.referrerBenefitPct >= 1, {
    message: "Una promo de referidos necesita el % para el socio que refiere (de 1 a 100)",
    path: ["referrerBenefitPct"],
});
export const createPromotionBody = z
    .object({
    name: z.string().trim().min(2).max(120),
    description: z.string().max(1000).default(""),
    type: z.enum(PROMOTION_TYPES),
    /** % en `discount_pct`, centavos en `fixed_amount`/`special_price`, días en `bonus_days`. */
    value: z.number().int().min(0),
    planIds: z.array(objectId).default([]),
    validFrom: dateISO,
    validUntil: dateISO,
    /** Etiqueta libre del catálogo `promotion_tags` (nuevo, reingreso, estudiante…). */
    eligibilityTag: z.string().trim().max(60).default(""),
    quota: z.number().int().min(1).nullable().default(null),
    referral: promotionReferral.default({ enabled: false, referrerBenefitPct: 0 }),
})
    .refine((p) => p.validUntil > p.validFrom, {
    message: "La vigencia hasta tiene que ser posterior a la vigencia desde",
    path: ["validUntil"],
})
    .refine((p) => p.type !== "discount_pct" || p.value <= 100, {
    message: "Un descuento porcentual no puede superar el 100%",
    path: ["value"],
});
export const updatePromotionBody = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    description: z.string().max(1000).optional(),
    planIds: z.array(objectId).optional(),
    validFrom: dateISO.optional(),
    validUntil: dateISO.optional(),
    eligibilityTag: z.string().trim().max(60).optional(),
    quota: z.number().int().min(1).nullable().optional(),
    /** Los créditos ya otorgados guardan su % (snapshot): cambiarlo rige para los próximos. */
    referral: promotionReferral.optional(),
    active: z.boolean().optional(),
});
export const listPromotionsQuery = pagination.extend({
    status: z.enum(["current", "scheduled", "expired", "inactive"]).optional(),
    planId: objectId.optional(),
});
/** Reporte de uso por promo, sede y período (RN-PLA-20). */
export const promotionUsageQuery = z.object({
    from: dateISO.optional(),
    to: dateISO.optional(),
    branchId: objectId.optional(),
});
/** Desactivar una promo es una decisión comercial: lleva motivo para el historial. */
export const deactivatePromotionBody = z.object({ reason: reasonRequired });
//# sourceMappingURL=planes.js.map